// The IRC layout's message rows (#154): one line per message in a black monospace pane, `[18:20] <nick> text`, the
// nick colored by a hash of the person, no avatars, bubbles or grouping. A reply is a dim `┊ <nick> excerpt` line
// above (the shared reply button, restyled in irc.css), reactions are a pink `*` line, a day change is a green
// `* Day changed` status line. Chosen in MessageList's MESSAGE_LAYOUTS. Its CSS is irc.css.
// Part of the IRC layout: deleting components/irc/ and its entries removes it.
import type { ReactNode } from "react";
import { useNow, useTimestampFormat } from "../../timestampFormat.ts";
import { dayLabel, type FrameProps, type MessageLayout, type RowItem } from "../MessageLayout.ts";
import { IrcAttachments } from "./IrcAttachments.tsx";
import { actionText, dayLine, nickColor, timeTag } from "./ircLogic.ts";
import "./irc.css";

/** "[18:20] " in the format chosen in Settings → Appearance → Timestamps. */
export function IrcTime({ ts }: { ts: number }) {
  const format = useTimestampFormat();
  const now = useNow(60_000);
  const tag = timeTag(ts, now, format);
  return tag ? <time className="irc-time" dateTime={new Date(ts).toISOString()}>{tag} </time> : null;
}

function IrcFrame({ it, name, quiet, tags, replyRef, children }: FrameProps) {
  const { m } = it;
  const action = actionText(m.body) !== null;
  return (
    <>
      {replyRef}
      <div className="irc-line">
        {!quiet && <IrcTime ts={m.ts} />}
        {action ? (
          <span className="irc-star">* {name} </span>
        ) : (
          <>
            <span className="irc-nick" style={{ color: nickColor(m.authorId) }}>&lt;{name}&gt;</span>
            {tags}{" "}
          </>
        )}
        {children}
      </div>
    </>
  );
}

function IrcStamp({ it }: { it: RowItem }) {
  return it.newDay ? <div className="irc-status">{dayLine(dayLabel(it.m.ts))}</div> : null;
}

/** A quiet system line (pinned a message, added it to a collection): `[18:20] * Sam pinned a message.` */
function IrcSystem({ it, children }: { it: RowItem; children: ReactNode }) {
  return (
    <div className="irc-line irc-sys">
      <IrcTime ts={it.m.ts} />
      {children}
    </div>
  );
}

const LINE_PX = 20;

export const ircMessages: MessageLayout = {
  id: "irc",
  Stamp: IrcStamp,
  rowClass: (it) => (actionText(it.m.body) !== null ? " irc-action" : ""),
  Frame: IrcFrame,
  Attachments: IrcAttachments,
  bodyText: (body) => {
    const a = actionText(body);
    return a === null ? body : a;
  },
  System: IrcSystem,
  Bubble: ({ children }) => <>{children}</>,
  stampPx: (it) => (it.newDay ? LINE_PX : 0),
  headPx: () => 2,
  replyPx: LINE_PX,
  textPx: (body, width) => {
    // 14px monospace is about 8.4px a character; the time and the nick take about 20 of them.
    const perLine = Math.max(16, (width - 40) / 8.4 - 20);
    return LINE_PX * body.split("\n").reduce((n, line) => n + Math.max(1, Math.ceil(line.length / perLine)), 0);
  },
  key: (it) => `${+it.newDay}`,
};
